import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import withAuthorization from "../HOC/with.authorization";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import {
  IEvent,
  ILocation,
  IServerEvent,
} from "../../interfaces/map.interfaces";
import { IFirebaseAuthUser } from "../../interfaces/authentication.interfaces";
import AuthenticationValidatorLogic from "../../logic/authenticationValidatorLogic";
import * as ROUTES from "../../constants/routes";
import StationImagesGallery from "../Map/StationImagesGallery";
import Spinner from "../Animations/Spinner";
import { readStationGalleryEvent } from "../../utils/station.utils";

function findEventById(
  locations: ILocation[],
  eventId: string
): IServerEvent | undefined {
  for (let i = 0; i < locations.length; i++) {
    const match = locations[i].events?.find(
      (event) => event.properties.id === eventId
    );
    if (match) {
      return match;
    }
  }
  return undefined;
}

function StationImagesPage() {
  const history = useHistory();
  const location = useLocation();
  const appContext: IAppContext = useContext(AppContext);
  const [eventObject, setEventObject] = useState<IEvent | null>(null);
  const [language, setLanguage] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const eventId = new URLSearchParams(location.search).get("eventId");

  useEffect(() => {
    appContext.services.userService.getMyData().then((data) => {
      setLanguage(data.data.language);
    });
  }, [appContext.services.userService]);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    setNotFound(false);
    const cachedEvent = readStationGalleryEvent(eventId);
    setEventObject(cachedEvent);
    setLoading(!cachedEvent);
    if (cachedEvent) {
      setEventObject(cachedEvent);
      setNotFound(false);
      setLoading(false);
    }

    let cancelled = false;
    let inFlight = false;
    let hasEvent = !!cachedEvent;
    const refreshEvent = async () => {
      if (cancelled || inFlight || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const data: any = await appContext.services.eventService.getMyData();
        if (cancelled) return;
        const match = findEventById(data?.data || [], eventId);
        if (!match) {
          setNotFound(true);
          setEventObject(null);
          hasEvent = false;
          return;
        }
        setEventObject({
          type: "Point",
          coordinates: match.geometry.coordinates,
          properties: match.properties,
        });
        hasEvent = true;
        setNotFound(false);
      } catch {
        if (!cancelled && !hasEvent) setNotFound(true);
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };
    refreshEvent();
    window.addEventListener("focus", refreshEvent);
    document.addEventListener("visibilitychange", refreshEvent);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refreshEvent);
      document.removeEventListener("visibilitychange", refreshEvent);
    };
  }, [eventId, appContext.services.eventService]);

  const closeGallery = () => {
    window.close();
    setTimeout(() => {
      if (!window.closed) {
        history.push(ROUTES.MAP.route);
      }
    }, 150);
  };

  if (loading) {
    return <Spinner />;
  }

  if (notFound || !eventObject) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        {language === "de"
          ? "Station-Ereignis nicht gefunden."
          : "Station event not found."}
      </div>
    );
  }

  return (
    <StationImagesGallery
      key={eventId || "station"}
      eventObject={eventObject}
      closeGallery={closeGallery}
      language={language}
    />
  );
}

const condition = (authUser: IFirebaseAuthUser) =>
  AuthenticationValidatorLogic.isAuthenticated(authUser);

export default withAuthorization(condition)(ROUTES.SIGN_IN.route)(
  StationImagesPage
);
